import { equipmentPayload } from "@/lib/nftIdentity.js";
// @/plugins/2.pinia/modules/nfts/services/nftService.js
import { $apiV5 } from "@/plugins/axios";
import {
  equipMechNft,
  equipOrbNft,
  equipPilotNft,
} from "@/api/nfts";
import { getSelfEquippedItems, getSelfUid } from "@/api/user.js";
import { isNftStaked } from "../types/nftTypes";

import { useUserStore } from "@/plugins/2.pinia/modules/user";
import { store } from "@store";

const userStore = useUserStore(store);

// Supported blockchain networks
export const SUPPORTED_CHAINS = {
  ETHEREUM: "eth",
  AVALANCHE: "avalanche",
  POLYGON: "polygon",
};

// Default chain to use - can be configured
const DEFAULT_CHAIN = SUPPORTED_CHAINS.AVALANCHE;

export const NftService = {
  /**
   * Fetch NFTs from the API
   * @param {string} chain - The blockchain to query (eth, avalanche, polygon)
   * @param {number} page - Page number
   * @param {number} pageSize - Items per page
   * @returns {Promise<Array>} The NFTs
   */
  async fetchNfts(
    chain = DEFAULT_CHAIN,
    page = 1,
    pageSize = 100,
    forceRefresh = false
  ) {
    try {
      // Validate chain
      if (!Object.values(SUPPORTED_CHAINS).includes(chain)) {
        console.warn(
          `Invalid chain: ${chain}, using default: ${DEFAULT_CHAIN}`
        );
        chain = DEFAULT_CHAIN;
      }

      const response = await $apiV5.get("/nfts", {
        params: {
          chain,
          page,
          pageSize,
          forceRefresh: forceRefresh ? "true" : "false",
        },
      });

      // Handle API response format
      if (response.data && response.data.data) {
        return response.data.data;
      } else if (Array.isArray(response.data)) {
        return response.data;
      }

      console.warn("Unexpected API response format:", response.data);
      return [];
    } catch (error) {
      console.error("Failed to fetch NFTs:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch NFTs");
    }
  },

  /**
   * Fetch the current user's ID
   * @returns {Promise<string>} The user ID
   */
  async fetchUserId() {
    try {
      const userId = await getSelfUid();

      return userId;
    } catch (error) {
      console.error("❌ Failed to fetch userId from getSelfUid():", error);
      throw new Error("Failed to initialize user");
    }
  },

  /**
   * Fetch the currently equipped items for the user
   * @returns {Promise<Object>} The equipped items
   */
  async fetchEquippedItems({ isCurrent = () => true } = {}) {
    try {
      const { profile, equippedItems } = await getSelfEquippedItems();

      if (!isCurrent()) return null;
      userStore.setUserInfo(profile);

      if (!equippedItems) {
        console.warn("No equipped items returned from user profile");
        return {};
      }

      return equippedItems;
    } catch (error) {
      console.error("Failed to fetch user equipped items:", error);
      throw new Error("Failed to load equipped items");
    }
  },

  async equipPilot(item, chain) {
    if (isNftStaked(item)) throw new Error("Cannot equip a staked item");
    return equipPilotNft(equipmentPayload(item, chain));
  },
  async equipMech(item, chain) {
    if (isNftStaked(item)) throw new Error("Cannot equip a staked item");
    return equipMechNft(equipmentPayload(item, chain, "mech"));
  },
  async equipOrb(item, chain) {
    if (isNftStaked(item)) throw new Error("Cannot equip a staked item");
    return equipOrbNft(equipmentPayload(item, chain, "orb"));
  },
};
